package br.com.texeratx.boardgametimeapp.core.image

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.CameraAlt
import androidx.compose.material.icons.filled.PhotoLibrary
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import br.com.texeratx.boardgametimeapp.core.i18n.LocalStrings

/**
 * Estado que gerencia a escolha entre câmera e galeria.
 * Em plataformas sem câmera (Web, Desktop), [onLaunch] abre a galeria diretamente.
 */
class ImageSourceState(
    val showDialog: Boolean,
    val onLaunch: () -> Unit,
    val onSelectGallery: () -> Unit,
    val onSelectCamera: () -> Unit,
    val onDismiss: () -> Unit
)

/**
 * Composable helper que encapsula os dois launchers e o estado do diálogo.
 * Use [ImageSourceState.onLaunch] no botão "Selecionar imagem".
 * Adicione [ImageSourcePickerDialog] em qualquer lugar da árvore de composição da tela.
 */
@Composable
fun rememberImageSourceState(onResult: (PickedImage?) -> Unit): ImageSourceState {
    var showDialog by remember { mutableStateOf(false) }
    val launchGallery = rememberImagePickerLauncher(onResult)
    val launchCamera = rememberCameraLauncher(onResult)

    return ImageSourceState(
        showDialog = showDialog,
        onLaunch = {
            if (hasCameraSupport) showDialog = true else launchGallery()
        },
        onSelectGallery = {
            showDialog = false
            launchGallery()
        },
        onSelectCamera = {
            showDialog = false
            launchCamera()
        },
        onDismiss = { showDialog = false }
    )
}

/**
 * Diálogo que aparece em Android/iOS para o usuário escolher entre câmera e galeria.
 * Não renderiza nada quando [state.showDialog] é false.
 */
@Composable
fun ImageSourcePickerDialog(state: ImageSourceState) {
    if (!state.showDialog) return

    val S = LocalStrings.current

    AlertDialog(
        onDismissRequest = state.onDismiss,
        title = { Text(S.escolherFonteImagem, style = MaterialTheme.typography.titleLarge) },
        text = {
            Column {
                ImageSourceOption(
                    icon = { Icon(Icons.Default.CameraAlt, contentDescription = null, modifier = Modifier.size(26.dp)) },
                    label = S.camera,
                    onClick = state.onSelectCamera
                )
                HorizontalDivider(modifier = Modifier.padding(horizontal = 4.dp))
                ImageSourceOption(
                    icon = { Icon(Icons.Default.PhotoLibrary, contentDescription = null, modifier = Modifier.size(26.dp)) },
                    label = S.galeria,
                    onClick = state.onSelectGallery
                )
            }
        },
        confirmButton = {},
        dismissButton = {
            TextButton(onClick = state.onDismiss) {
                Text(S.cancel)
            }
        }
    )
}

@Composable
private fun ImageSourceOption(
    icon: @Composable () -> Unit,
    label: String,
    onClick: () -> Unit
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clickable(onClick = onClick)
            .padding(vertical = 14.dp, horizontal = 4.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        icon()
        Spacer(Modifier.width(16.dp))
        Text(label, style = MaterialTheme.typography.bodyLarge)
    }
}
